/**
 * DataTable - Reusable responsive data table organism.
 *
 * Renders a styled desktop table (hidden on mobile) alongside an optional
 * mobile card layout (hidden on tablet+). Provides a consistent look and feel
 * for list pages across the platform.
 *
 * Features:
 *	 - Generic column definitions with custom cell renderers
 *	 - Responsive: desktop table with overflow scroll + mobile cards
 *	 - Loading skeleton support (custom or auto-generated)
 *	 - Empty state message
 *	 - Optional row click handler
 *	 - Consistent brand styling (white card, rounded border, header bg)
 *
 * Usage:
 *	 <DataTable
 *		 columns={[
 *			 { key: 'name', header: 'Name', render: (item) => item.name },
 *			 { key: 'status', header: 'Status', render: (item) => <Badge>{item.status}</Badge> },
 *		 ]}
 *		 data={buildings}
 *		 keyExtractor={(b) => b.id}
 *		 renderMobileCard={(b) => <BuildingCard building={b} />}
 *		 isLoading={isLoading}
 *		 emptyMessage="No buildings found."
 *	 />
 */
import React from 'react';

/* ============================================
 * TYPE DEFINITIONS
 * ============================================ */

/** Defines a single column in the DataTable */
export interface DataTableColumn<T> {
	/** Unique key for this column (used as React key) */
	key: string;
	/** Header label text or ReactNode (empty string for icon-only columns) */
	header: string | React.ReactNode;
	/** Render function for the cell content — receives the data item */
	render: (item: T) => React.ReactNode;
	/** Optional extra className applied to the <th> header cell */
	headerClassName?: string;
	/** Optional extra className applied to each <td> body cell */
	cellClassName?: string;
}

export interface DataTableProps<T> {
	/** Column definitions — each specifies header text and a cell render function */
	columns: DataTableColumn<T>[];
	/** Array of data items to render as rows */
	data: T[];
	/** Extract a unique key from each data item for React reconciliation */
	keyExtractor: (item: T) => string | number;
	/** Optional click handler for an entire row — adds pointer cursor and hover state */
	onRowClick?: (item: T) => void;
	/** Optional render function for the mobile card layout.
	 *	If omitted, mobile view falls back to a simplified auto-generated card. */
	renderMobileCard?: (item: T) => React.ReactNode;
	/** Optional pre-row that spans every column and renders ABOVE each
	 *	data row on the desktop table. Useful when a row has a "summary"
	 *	field (e.g. Building + Customer) that doesn't belong in any of
	 *	the regular columns. When omitted only the standard row is
	 *	rendered. */
	renderRowHeader?: (item: T) => React.ReactNode;
	/** Whether data is currently loading — shows skeleton rows when true */
	isLoading?: boolean;
	/** Custom loading content to render inside <tbody> while loading.
	 *	If omitted, a default skeleton is generated based on column count. */
	loadingContent?: React.ReactNode;
	/** Number of skeleton rows to show when using the default skeleton (default: 6) */
	skeletonRows?: number;
	/** Message shown when data is empty and not loading */
	emptyMessage?: string;
	/** Optional extra className applied to the outer container */
	className?: string;
}

/* ============================================
 * DEFAULT SKELETON
 * Generates animated placeholder rows while data loads.
 * ============================================ */

/** Single skeleton row — renders pulse-animated bars for each column */
const SkeletonRow: React.FC<{ columnCount: number }> = ({ columnCount }) => (
	<tr className="border-b border-brand-border">
		{Array.from({ length: columnCount }).map((_, i) => (
			<td key={i} className="py-12px px-16px">
				{/* Animated placeholder bar — varies width for visual variety */}
				<div
					className="h-14px bg-gray-100 animate-pulse rounded-4px"
					style={{ width: `${60 + ((i * 23) % 40)}%` }}
				/>
			</td>
		))}
	</tr>
);

/* ============================================
 * COMPONENT
 * ============================================ */

/* ============================================
 * DEFAULT MOBILE CARD
 * Auto-generated stacked card used on mobile when a consumer does
 * not supply renderMobileCard — maps each column to a label/value
 * row so the table never collapses to a blank screen on phones.
 * ============================================ */
function DefaultMobileCard<T>({ columns, item }: { columns: DataTableColumn<T>[]; item: T }) {
	return (
		<div className="bg-white border border-brand-border rounded-lg p-16px shadow-sm">
			<div className="flex flex-col gap-8px">
				{columns.map((col) => {
					const headerText = typeof col.header === 'string' ? col.header : '';
					return (
						<div key={col.key} className="flex items-start justify-between gap-12px">
							{headerText && (
								<span className="text-11px font-semibold uppercase tracking-wide text-brand-text-muted flex-shrink-0 pt-2px">{headerText}</span>
							)}
							<div className={`text-14px text-brand-text min-w-0 ${headerText ? 'text-right ml-auto' : 'w-full'}`}>{col.render(item)}</div>
						</div>
					);
				})}
			</div>
		</div>
	);
}

/** Generic DataTable — use type parameter to get typed column render functions */
export function DataTable<T>({
	columns,
	data,
	keyExtractor,
	onRowClick,
	renderMobileCard,
	renderRowHeader,
	isLoading = false,
	loadingContent,
	skeletonRows = 6,
	emptyMessage = 'No results found.',
	className = '',
}: DataTableProps<T>) {
	/** Whether to show the table (during loading for skeletons, or when data exists) */
	const showTable = isLoading || data.length > 0;

	return (
		<div className={`data-table ${className}`} data-ref="organism-data-table">
			{/* === EMPTY STATE === */}
			{/* Shown when not loading and there is no data */}
			{!isLoading && data.length === 0 && (
				<div className="data-table__empty bg-white rounded-12px border border-brand-border p-24px text-center">
					<p className="text-14px text-brand-text-muted py-32px">
						{emptyMessage}
					</p>
				</div>
			)}

			{/* === DATA TABLE === */}
			{showTable && (
				<>
					{/* --- Desktop table --- hidden on mobile, visible on tablet+ */}
					<div className="hidden tablet:block bg-white rounded-12px border border-brand-border shadow-sm overflow-hidden">
						<div className="overflow-x-auto">
							<table className="data-table__table w-full text-14px font-sans">
								{/* Table header row — light surface background */}
								<thead>
									<tr className="bg-brand-surface border-b border-brand-border">
										{columns.map((col) => (
											<th
												key={col.key}
												className={`text-left py-12px px-16px font-semibold text-brand-text-muted text-12px uppercase tracking-wide ${col.headerClassName || ''}`}
											>
												{col.header}
											</th>
										))}
									</tr>
								</thead>
								<tbody>
									{/* Loading skeleton rows */}
									{isLoading && (
										loadingContent || (
											<>
												{Array.from({ length: skeletonRows }).map((_, i) => (
													<SkeletonRow key={`skel-${i}`} columnCount={columns.length} />
												))}
											</>
										)
									)}

									{/* Actual data rows. When renderRowHeader is
									    supplied, each item produces two rows: a
									    full-width summary row (e.g. Building +
									    Customer for inspections) followed by
									    the standard columned row. The two are
									    visually grouped with a top border on
									    the header and no border between them. */}
									{!isLoading && data.map((item) => (
										<React.Fragment key={keyExtractor(item)}>
											{renderRowHeader && (
												<tr
													className={`bg-brand-surface/40 ${onRowClick ? 'cursor-pointer' : ''}`}
													onClick={onRowClick ? () => onRowClick(item) : undefined}
												>
													<td
														colSpan={columns.length}
														className="px-16px pt-12px pb-4px"
													>
														{renderRowHeader(item)}
													</td>
												</tr>
											)}
											<tr
												className={`data-table__row border-b border-brand-border hover:bg-brand-surface transition-colors ${onRowClick ? 'cursor-pointer' : ''}`}
												onClick={onRowClick ? () => onRowClick(item) : undefined}
											>
												{columns.map((col) => (
													<td
														key={col.key}
														className={`data-table__cell ${renderRowHeader ? 'pt-4px pb-12px' : 'py-12px'} px-16px ${col.cellClassName || ''}`}
													>
														{col.render(item)}
													</td>
												))}
											</tr>
										</React.Fragment>
									))}
								</tbody>
							</table>
						</div>
					</div>

					{/* --- Mobile card layout --- visible below tablet breakpoint */}
					{(
						<div className="tablet:hidden flex flex-col gap-12px">
							{/* Loading skeleton cards for mobile */}
							{isLoading && Array.from({ length: skeletonRows }).map((_, i) => (
								<div key={`mob-skel-${i}`} className="bg-white border border-brand-border rounded-lg p-16px animate-pulse">
									<div className="h-14px w-3/4 bg-gray-100 rounded-4px mb-8px" />
									<div className="h-12px w-1/2 bg-gray-50 rounded-4px mb-4px" />
									<div className="h-12px w-2/3 bg-gray-50 rounded-4px" />
								</div>
							))}

							{/* Actual data cards */}
							{!isLoading && data.map((item) => (
								<React.Fragment key={keyExtractor(item)}>
									{renderMobileCard ? renderMobileCard(item) : <DefaultMobileCard columns={columns} item={item} />}
								</React.Fragment>
							))}
						</div>
					)}
				</>
			)}
		</div>
	);
}
